import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { changeSubscriptionPlan } from "@/lib/billing/changePlan";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Tier key sent to change-subscription-plan (e.g. "broker", "carrier_full"). */
  tier: string;
  /** Human label for the target plan, e.g. "Full Access — $99.99/month per user". */
  tierLabel: string;
  seats?: number;
  onChanged?: () => void;
}

/**
 * Confirm dialog for switching an EXISTING subscription to another tier.
 * The copy must state the prorated charge — the user is not starting a new
 * subscription, their current one is modified in place.
 */
export function ChangePlanDialog({ open, onOpenChange, tier, tierLabel, seats, onChanged }: Props) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const confirm = async () => {
    setLoading(true);
    const result = await changeSubscriptionPlan(tier, seats);
    setLoading(false);

    if (result.ok !== true) {
      toast.error(result.message);
      return;
    }

    toast.success("Plan updated. Your access changes within a few seconds.");
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["billing-mode"] }),
      queryClient.invalidateQueries({ queryKey: ["company-account"] }),
      queryClient.invalidateQueries({ queryKey: ["subscription"] }),
    ]);
    onOpenChange(false);
    onChanged?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Switch to {tierLabel}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>
                Your current subscription will be changed to <strong>{tierLabel}</strong>. You are
                not starting a second subscription.
              </p>
              <p>
                Stripe charges the <strong>prorated difference</strong> for the remainder of your
                current billing period, then bills the new rate going forward. Downgrades apply a
                prorated credit instead.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); confirm(); }}
            disabled={loading}
          >
            {loading ? "Updating…" : "Confirm plan change"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
